
import api from '../utils/api';

export interface RoundRobinGroup {
  group_id: number;
  uuid: string;
  group_name: string;
  column_id: number;
  active: boolean;
  current_user_index: number;
  column: {
    col_id: number;
    col_label: string;
    col_name: string;
  };
  groupOptions: {
    id: number;
    option: {
      opt_id: number;
      opt_name: string;
      opt_value: string;
    };
  }[];
  groupUsers: {
    id: number;
    user_order: number;
    user: {
      userId: number;
      userName: string;
      userEmail: string;
    };
  }[];
}

export interface CreateRoundRobinGroupData {
  group_name: string;
  column_id: number;
  option_ids: number[];
  user_ids: number[];
}

export interface AvailableColumn {
  col_id: number;
  col_label: string;
  col_name: string;
  options: {
    opt_id: number;
    opt_name: string;
    opt_value: string;
  }[];
}

export interface AvailableUser {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
}

// Get all round robin groups
export async function getRoundRobinGroups() {
  try {
    const response = await api.get('/round-robin-groups');
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch round robin groups');
    }

    return {
      success: true,
      data: response.data as RoundRobinGroup[],
    };
  } catch (error) {
    console.error('Error fetching round robin groups:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Get single round robin group by ID
export async function getRoundRobinGroupById(id: number) {
  try {
    const response = await api.get(`/round-robin-groups/${id}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch round robin group');
    }

    return {
      success: true,
      data: response.data as RoundRobinGroup,
    };
  } catch (error) {
    console.error('Error fetching round robin group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Create new round robin group
export async function createRoundRobinGroup(data: CreateRoundRobinGroupData) {
  try {
    const response = await api.post('/round-robin-groups', data);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to create round robin group');
    }

    return {
      success: true,
      data: response.data as RoundRobinGroup,
      message: response.message,
    };
  } catch (error) {
    console.error('Error creating round robin group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Update round robin group
export async function updateRoundRobinGroup(id: number, data: Partial<CreateRoundRobinGroupData>) {
  try {
    const response = await api.put(`/round-robin-groups/${id}`, data);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update round robin group');
    }

    return {
      success: true,
      data: response.data as RoundRobinGroup,
      message: response.message,
    };
  } catch (error) {
    console.error('Error updating round robin group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Delete round robin group
export async function deleteRoundRobinGroup(id: number) {
  try {
    const response = await api.delete(`/round-robin-groups/${id}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete round robin group');
    }

    return {
      success: true,
      message: response.message,
    };
  } catch (error) {
    console.error('Error deleting round robin group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Get available columns for round robin
export async function getAvailableColumns() {
  try {
    const response = await api.get('/round-robin-groups/columns');
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch available columns');
    }

    return {
      success: true,
      data: response.data as AvailableColumn[],
    };
  } catch (error) {
    console.error('Error fetching available columns:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Get available users for round robin
export async function getAvailableUsers() {
  try {
    // Use existing users API endpoint
    const response = await api.get('/users?limit=100');
    const responseData = response.data as { users?: any[] };

    // Check if we have data
    if (!responseData || !responseData.users) {
      console.error('No users data in response:', response);
      return {
        success: false,
        error: 'No users data available',
      };
    }

    // Transform the data to match our AvailableUser interface
    const users = responseData.users.filter((user: any) => user.user_active).map((user: any) => ({
      userId: user.userId,  // Using actual userId field (numeric)
      firstName: user.userName?.split(' ')[0] || user.userName || '',
      lastName: user.userName?.split(' ').slice(1).join(' ') || '',
      email: user.userEmail,
    }));

    return {
      success: true,
      data: users as AvailableUser[],
    };
  } catch (error) {
    console.error('Error fetching available users:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}